import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <aromely-diffuser-mvp> — purchase area for the diffuser PDP MVP.
 *
 * One state object drives every price and label on the page (hero price, mode cards,
 * quantity label, summary, Add to cart, sticky bar). There is no second pricing path.
 *
 *   state.mode   'solo' | 'set'
 *   state.qty    1..10   (in set mode this is the number of SETS)
 *   state.scents Map<productId, { pid, name, url, size: 'small'|'large' }>  (max 3)
 *   state.place  'hvac' | 'standalone'  (guidance only — never changes price or cart)
 *
 * Cart: one POST to Theme.routes.cart_add_url with every line of the configuration
 * (Shopify adds all or nothing), then the theme's CartAddEvent opens the existing drawer.
 * Set discount: OFF unless the section switch is on; then the configured code is applied
 * through /cart/update.js and the response is checked before anything is promised.
 *
 * Fit helper: imports assets/aromely-fit-engine.js (shared math, 1.2 allowance) lazily.
 * If the import fails the helper hides itself and the purchase path is untouched.
 */
class AromelyDiffuserMvp extends Component {
  #state = { mode: 'solo', qty: 1, scents: new Map(), place: 'hvac', pickerOpen: false, busy: false };
  #fit = { preset: null, ceiling: 8, systems: 1, units: null, engine: null };
  #ac = null;
  #sticky = null;
  #stickyIO = null;
  #drawerMO = null;
  #stickyBtnHandler = null;
  #maxScents = 3;

  connectedCallback() {
    super.connectedCallback();
    // A new controller per connection: the theme editor re-connects elements, and an aborted
    // controller would leave every listener dead and the sticky handler attached twice.
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const d = this.dataset;
    this.#maxScents = Number(d.maxScents) || 3;
    this.#state.place = this.querySelector('[data-place][aria-pressed="true"]')?.dataset.place || (this.querySelector('[data-place]')?.dataset.place ?? 'hvac');

    // Buttons/inputs use the theme's declarative on:click / on:change / on:input attributes
    // (component.js delegates them to methods on this element). <details> toggle does not bubble,
    // so it is bound here.
    this.refs.fit?.addEventListener('toggle', () => { if (this.refs.fit.open) this.#loadFitEngine(); }, { signal });

    // "Add fragrance to this order" links in the lower page.
    const section = this.closest('.adm');
    section?.querySelectorAll('[data-adm-jump="set"]').forEach((a) =>
      a.addEventListener('click', (e) => { e.preventDefault(); this.chooseSet(); }, { signal })
    );

    this.#setupSticky(section);
    this.#renderSetFromPrice();
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
    this.#stickyIO?.disconnect();
    this.#drawerMO?.disconnect();
    if (this.#sticky && this.#stickyBtnHandler) this.#sticky.querySelector('[data-sticky-btn]')?.removeEventListener('click', this.#stickyBtnHandler);
    this.#setStickyVisible(false);
  }

  /* ───────────────────────── money & pricing ───────────────────────── */

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }
  get #diffuserCents() { return Number(this.dataset.priceCents) || 0; }
  get #discountPct() {
    return this.dataset.setDiscountEnabled === 'true' ? Math.min(100, Math.max(0, Number(this.dataset.setDiscountPct) || 0)) : 0;
  }
  #scentEl(pid) { return this.querySelector(`[data-scent][data-pid="${pid}"]`); }
  #scentVariant(pid, size) {
    const el = this.#scentEl(pid);
    if (!el) return null;
    const vid = el.dataset[`${size}Vid`];
    const price = Number(el.dataset[`${size}Price`]);
    if (!vid || !Number.isFinite(price)) return null;
    return { vid, price };
  }
  /** Discounted price of one bottle in cents (display only; the real discount is Shopify's). Rounded to the cent like Shopify. */
  #bottleCents(price) {
    const pct = this.#discountPct;
    return pct ? price - Math.round((price * pct) / 100) : price;
  }
  /** October Sale percentage while the promotion is live in the browser (from the promo state), else 0. */
  get #promoPct() {
    const promo = window.AromelyPromo;
    if (!promo || typeof promo.active !== 'function' || !promo.active()) return 0;
    return Number(promo.state?.pct) || 0;
  }
  /** Sale price of one unit in cents; Shopify truncates the OCTSALE discount per unit. */
  #saleCents(cents) {
    const pct = this.#promoPct;
    return pct > 0 ? cents - Math.floor((cents * pct) / 100) : cents;
  }
  /** Repaint the shared price line: sale price, struck full price and the sale badge. */
  #paintSaleLine(priceEl, compareEl, badgeEl, fullCents, suffix = '', saleOverride) {
    const pct = this.#promoPct;
    const sale = saleOverride ?? this.#saleCents(fullCents);
    const show = pct > 0 && fullCents > sale;
    if (priceEl) priceEl.textContent = `${this.#money(sale)}${suffix}`;
    if (compareEl) { compareEl.hidden = !show; compareEl.textContent = show ? this.#money(fullCents) : ''; }
    if (badgeEl) {
      const wrap = priceEl?.closest('[data-promo-label]');
      const prefix = wrap?.dataset.promoLabel || `${window.AromelyPromo?.state?.name || 'Sale'} · −`;
      badgeEl.textContent = show ? `${prefix}${pct}%` : '';
      badgeEl.classList.toggle('hidden', !show);
      badgeEl.classList.toggle('inline-flex', show);
    }
    return sale;
  }
  /** Price of one unit of the current configuration (diffuser [+ bottles]). */
  #unitCents() {
    let total = this.#diffuserCents;
    if (this.#state.mode === 'set') {
      for (const s of this.#state.scents.values()) {
        const v = this.#scentVariant(s.pid, s.size);
        if (v) total += this.#promoPct > 0 ? v.price : this.#bottleCents(v.price);
      }
    }
    return total;
  }
  /** Sum discounted real product units before applying the number of sets. */
  #saleUnitCents() {
    if (this.#promoPct <= 0) return this.#unitCents();
    let total = this.#saleCents(this.#diffuserCents);
    if (this.#state.mode === 'set') {
      for (const scent of this.#state.scents.values()) {
        const variant = this.#scentVariant(scent.pid, scent.size);
        if (variant) total += this.#saleCents(variant.price);
      }
    }
    return total;
  }
  get #setIncomplete() { return this.#state.mode === 'set' && this.#state.scents.size === 0; }

  #renderSetFromPrice() {
    const el = this.refs.setFromPrice;
    if (!el) return;
    let cheapest = Infinity;
    this.querySelectorAll('[data-scent]').forEach((s) => {
      ['small', 'large'].forEach((size) => {
        const p = Number(s.dataset[`${size}Price`]);
        if (s.dataset[`${size}Vid`] && Number.isFinite(p) && p < cheapest) cheapest = p;
      });
    });
    const fromPrice = this.#promoPct > 0
      ? this.#saleCents(this.#diffuserCents) + this.#saleCents(cheapest)
      : this.#diffuserCents + this.#bottleCents(cheapest);
    el.textContent = Number.isFinite(cheapest) ? `from ${this.#money(fromPrice)}` : '';
  }

  /* ───────────────────────── mode / picker ───────────────────────── */

  onModeChange() {
    const checked = this.querySelector('input[type="radio"][name^="adm-mode"]:checked');
    const mode = checked?.value === 'set' ? 'set' : 'solo';
    if (mode === this.#state.mode) return;
    this.#state.mode = mode;
    if (mode === 'set') this.#state.pickerOpen = true; // never collapse on its own
    this.render();
  }

  chooseSet() {
    const setRadio = this.querySelector('input[type="radio"][name^="adm-mode"][value="set"]');
    if (setRadio && !setRadio.checked) { setRadio.checked = true; this.#state.mode = 'set'; }
    this.#state.pickerOpen = true;
    this.render();
    (this.refs.picker || this).scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.refs.picker?.querySelector('.adm__scent-pick')?.focus({ preventScroll: true });
  }

  toggleScent(event) {
    const card = event.target.closest('[data-scent]');
    const pid = card?.dataset.pid;
    if (!pid) return;
    const { scents } = this.#state;
    if (scents.has(pid)) {
      scents.delete(pid);
    } else {
      if (scents.size >= this.#maxScents) {
        this.#note(`You can pick up to ${this.#maxScents} fragrances. Remove one to swap it.`, false);
        return;
      }
      const preferred = this.refs.picker?.dataset.defaultSize === 'large' ? 'large' : 'small';
      const size = this.#scentVariant(pid, preferred) ? preferred : (this.#scentVariant(pid, 'small') ? 'small' : 'large');
      scents.set(pid, { pid, name: card.dataset.name, url: card.dataset.url, size });
    }
    this.render();
  }

  setScentSize(event) {
    const btn = event.target;
    const card = btn.closest('[data-scent]');
    const s = card && this.#state.scents.get(card.dataset.pid);
    if (!s || btn.disabled) return;
    s.size = btn.dataset.size === 'large' ? 'large' : 'small';
    this.render();
  }

  removeScent(pid) {
    this.#state.scents.delete(pid);
    this.render();
  }

  pickerDone() {
    if (this.#setIncomplete) { this.#note('Choose at least one fragrance to complete the set.', false); return; }
    this.#state.pickerOpen = false;
    this.render();
    this.refs.addBtn?.focus({ preventScroll: true });
  }

  pickerEdit() {
    this.#state.pickerOpen = true;
    this.render();
    this.refs.picker?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  #note(text, ok) {
    const el = this.refs.pickNote;
    if (!el) return;
    el.textContent = text;
    el.dataset.ok = ok ? 'true' : 'false';
  }

  /* ───────────────────────── quantity ───────────────────────── */

  qtyUp() { if (this.#state.qty < 10) { this.#state.qty += 1; this.render(); } }
  qtyDown() { if (this.#state.qty > 1) { this.#state.qty -= 1; this.render(); } }

  /* ───────────────────────── render ───────────────────────── */

  render() {
    const st = this.#state;
    const r = this.refs;
    const name = (this.dataset.productName || 'diffuser') + (this.dataset.variantTitle ? ` (${this.dataset.variantTitle})` : '');
    const n = st.scents.size;
    const unit = this.#unitCents();
    const saleUnit = this.#saleUnitCents();
    const incomplete = this.#setIncomplete;
    const plural = (k, w) => `${k} ${w}${k === 1 ? '' : 's'}`;

    // Hero — sale price while the October Sale is live, full price struck beside it
    this.#paintSaleLine(r.heroPrice, r.heroCompare, r.heroBadge, unit, incomplete ? ' + fragrance' : '', saleUnit);
    const saleTotal = saleUnit * st.qty;
    if (r.heroLabel) {
      r.heroLabel.textContent = st.mode === 'solo' ? 'Diffuser only'
        : incomplete ? 'Diffuser + fragrance: choose 1 to 3 fragrances'
        : `Diffuser + ${plural(n, 'fragrance')}`;
    }

    // Picker visibility & cards
    if (r.picker) r.picker.hidden = !(st.mode === 'set' && st.pickerOpen);
    if (r.setSummary) r.setSummary.hidden = !(st.mode === 'set' && !st.pickerOpen && n > 0);
    this.querySelectorAll('[data-scent]').forEach((card) => {
      const s = st.scents.get(card.dataset.pid);
      card.querySelector('.adm__scent-pick')?.setAttribute('aria-pressed', s ? 'true' : 'false');
      const sizes = card.querySelector('.adm__scent-size');
      if (sizes) {
        sizes.hidden = !s;
        sizes.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', s && b.dataset.size === s.size ? 'true' : 'false'));
      }
    });
    if (r.pickCount) r.pickCount.textContent = `${n} of ${this.#maxScents} chosen`;
    if (r.pickList) {
      r.pickList.replaceChildren(...[...st.scents.values()].map((s) => {
        const v = this.#scentVariant(s.pid, s.size);
        const label = r.picker?.dataset[`${s.size}Label`] || s.size;
        const li = document.createElement('li');
        const span = document.createElement('span');
        span.innerHTML = `<b></b> · <em></em>`;
        span.querySelector('b').textContent = s.name;
        span.querySelector('em').textContent = `${label}${v ? ` · ${this.#money(this.#promoPct > 0 ? this.#saleCents(v.price) : this.#bottleCents(v.price))}` : ''}`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = 'Remove';
        btn.setAttribute('aria-label', `Remove ${s.name}`);
        btn.addEventListener('click', () => this.removeScent(s.pid));
        li.append(span, btn);
        return li;
      }));
    }
    if (r.pickDone) r.pickDone.disabled = n === 0;
    if (n === 0) this.#note('Choose at least one fragrance to complete the set.', false);
    else this.#note(n < this.#maxScents ? `Add up to ${this.#maxScents - n} more, or tap Done.` : 'That\'s the full set. Tap Done.', true);
    if (r.setSummaryText) {
      r.setSummaryText.textContent = [...st.scents.values()].map((s) => `${s.name} (${(r.picker?.dataset[`${s.size}Label`] || s.size).split(' (')[0]})`).join(', ');
    }

    // Quantity + summary
    if (r.qtyLabel) r.qtyLabel.textContent = st.mode === 'set' ? 'Number of sets' : 'Quantity';
    if (r.qtyValue) r.qtyValue.value = String(st.qty);
    if (r.qtyMinus) r.qtyMinus.disabled = st.qty <= 1;
    if (r.qtyPlus) r.qtyPlus.disabled = st.qty >= 10;
    let summary;
    if (st.mode === 'solo') summary = `${plural(st.qty, `${name} diffuser`)}. Fragrance oil sold separately.`;
    else if (incomplete) summary = `${plural(st.qty, 'set')}: ${plural(st.qty, `${name} diffuser`)} + fragrance still to choose.`;
    else summary = `${plural(st.qty, 'set')}: ${plural(st.qty, 'diffuser')} + ${plural(st.qty * n, 'bottle')} (${[...st.scents.values()].map((s) => s.name).join(', ')}).`;
    if (r.summary) { r.summary.textContent = summary; r.summary.dataset.incomplete = incomplete ? 'true' : 'false'; }

    // Add to cart
    const available = this.dataset.variantAvailable !== 'false';
    if (r.addBtn) {
      r.addBtn.dataset.mode = incomplete ? 'choose' : 'add';
      r.addBtn.disabled = !available || st.busy;
      if (r.addLabel) r.addLabel.textContent = !available ? 'Sold out' : incomplete ? 'Choose fragrance' : `Add to cart · ${this.#money(saleTotal)}`;
    }

    // Sticky bar mirrors the same numbers
    if (this.#sticky) {
      const price = this.#sticky.querySelector('[data-sticky-price]');
      const sum = this.#sticky.querySelector('[data-sticky-summary]');
      const btn = this.#sticky.querySelector('[data-sticky-btn]');
      if (price) price.textContent = incomplete ? `${this.#money(saleTotal)} + fragrance` : this.#money(saleTotal);
      if (sum) sum.textContent = st.mode === 'solo' ? `${plural(st.qty, name)} · Diffuser only` : incomplete ? `${name} set · choose 1 to 3 fragrances` : `${plural(st.qty, 'set')} · ${plural(n, 'fragrance')}`;
      if (btn) { btn.dataset.mode = incomplete ? 'choose' : 'add'; btn.textContent = !available ? 'Sold out' : incomplete ? 'Choose fragrance' : 'Add to cart'; btn.disabled = !available || st.busy; }
    }

    // Guidance
    this.refs.placeBtns?.forEach?.((b) => b.setAttribute('aria-pressed', b.dataset.place === st.place ? 'true' : 'false'));
    if (r.fitSystemsWrap) r.fitSystemsWrap.hidden = st.place !== 'hvac';
  }

  /* ───────────────────────── cart ───────────────────────── */

  async addToCart() {
    if (this.#state.busy) return;
    if (this.#setIncomplete) { this.chooseSet(); return; }
    const items = [{ id: Number(this.dataset.variantId), quantity: this.#state.qty }];
    if (this.#state.mode === 'set') {
      for (const s of this.#state.scents.values()) {
        const v = this.#scentVariant(s.pid, s.size);
        if (!v) { this.#error(`${s.name} isn't available in that size any more. Pick another size or remove it.`); return; }
        items.push({ id: Number(v.vid), quantity: this.#state.qty });
      }
    }

    this.#state.busy = true;
    this.#error('');
    const btn = this.refs.addBtn;
    if (btn) btn.dataset.state = 'loading';
    this.render();
    // Baseline from the header badge (hidden bubble = empty cart) — no pre-flight request, so the
    // add itself is the first network call and the drawer opens sooner.
    const bubble = document.querySelector('[data-aromely-cart-bubble]');
    const before = bubble
      ? (bubble.classList.contains('hidden') ? 0 : parseInt(bubble.querySelector('[data-aromely-cart-count]')?.textContent || '', 10))
      : NaN;

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        // Do not assume: a stock error can leave part of a multi-line request in the cart.
        await this.#explainFailure(`Couldn't add this to your cart: ${data.description || data.message || 'please try again'}.`, before);
        return;
      }

      let discountNote = '';
      if (this.#state.mode === 'set' && this.#discountPct > 0 && this.dataset.setDiscountCode) {
        discountNote = await this.#applySetDiscount(this.dataset.setDiscountCode);
      }

      // No second request here: the drawer fetches its own section on this event and syncs the
      // header badge from it.
      this.dispatchEvent(new CartAddEvent(data, this.id, {
        source: 'aromely-diffuser-mvp',
        productId: this.dataset.productId,
        variantId: this.dataset.variantId,
      }));
      if (discountNote) this.#error(discountNote);
    } catch (err) {
      console.error('[aromely-diffuser-mvp] add to cart failed', err);
      await this.#explainFailure('We couldn\'t confirm this with the store. Check your connection.', before);
    } finally {
      this.#state.busy = false;
      if (btn) btn.dataset.state = '';
      this.render();
    }
  }

  /** Compare the live cart with the count we started from and say what actually happened. */
  async #explainFailure(lead, before) {
    let cart = null;
    try {
      cart = await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((x) => x.json());
    } catch { /* unreachable: fall through to the neutral message */ }
    if (cart && !Number.isNaN(before) && cart.item_count !== before) {
      this.dispatchEvent(new CartAddEvent({ items: [] }, this.id, { source: 'aromely-diffuser-mvp', itemCount: cart.item_count }));
      this.#error(`${lead} Part of it did reach your cart. Please check the cart before trying again.`, true);
    } else if (cart && !Number.isNaN(before)) {
      this.#error(`${lead} Nothing was added.`, true);
    } else {
      this.#error(`${lead} Please check your cart before trying again.`, true);
    }
  }

  /** Applies the configured code and reports honestly if it did not take. */
  async #applySetDiscount(code) {
    // October Sale: the promotion code replaces the set code and may not apply yet
    // (below its minimum), which is not an error the shopper should see.
    if (window.AromelyPromo?.active?.()) {
      await window.AromelyPromo.reconcile?.();
      return '';
    }
    try {
      const res = await fetch(`${Theme.routes.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      const cart = await res.json();
      const applied = (cart?.discount_codes || []).some((c) => c.applicable && String(c.code).toUpperCase() === code.toUpperCase());
      return applied ? '' : 'Your set was added, but the set discount could not be applied to this cart. Our team can help: it may already have been used on your account.';
    } catch {
      return 'Your set was added, but we could not confirm the set discount. Check the cart total before checkout.';
    }
  }

  #error(text, retry = false) {
    const el = this.refs.error;
    if (!el) return;
    el.hidden = !text;
    el.textContent = text || '';
    if (text && retry) {
      const b = document.createElement('button');
      b.type = 'button'; b.textContent = 'Try again';
      b.addEventListener('click', () => this.addToCart());
      el.append(' ', b);
    }
  }

  /* ───────────────────────── sticky bar ───────────────────────── */

  #setupSticky(section) {
    this.#sticky = section?.querySelector(`[data-adm-sticky="${this.dataset.sectionId}"]`) || null;
    if (!this.#sticky || !this.refs.addBtn) return;
    this.#stickyBtnHandler = () => {
      if (this.#setIncomplete) this.chooseSet(); else this.addToCart();
    };
    this.#sticky.querySelector('[data-sticky-btn]')?.addEventListener('click', this.#stickyBtnHandler);

    let atcVisible = true;
    let drawerOpen = false;
    // Experiment pro-sticky: data-sticky-mode="always" shows the bar from first paint on screens under 990 px
    // (hidden only while the cart drawer is open). Default "scroll" = the behaviour below.
    const alwaysOn = () => this.dataset.stickyMode === 'always' && window.innerWidth < 990;
    // Desktop too: at 1440×900 the buy button sits below the fold, so the bar earns its place there as well.
    const update = () => this.#setStickyVisible(alwaysOn() ? !drawerOpen : (!atcVisible && !drawerOpen));

    // Show only once the shopper has scrolled PAST the add button (not while it is still below).
    const measure = () => { atcVisible = this.refs.addBtn.getBoundingClientRect().bottom > 0; };
    this.#stickyIO = new IntersectionObserver(() => { measure(); update(); }, { threshold: 0 });
    this.#stickyIO.observe(this.refs.addBtn);
    window.addEventListener('scroll', () => { measure(); update(); }, { passive: true, signal: this.#ac.signal });

    const dialog = document.querySelector('aromely-cart-drawer-component dialog, cart-drawer-component dialog');
    if (dialog) {
      drawerOpen = dialog.open;
      this.#drawerMO = new MutationObserver(() => { drawerOpen = dialog.open; update(); });
      this.#drawerMO.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
    document.addEventListener('cart:update', () => { drawerOpen = true; update(); setTimeout(() => { drawerOpen = dialog ? dialog.open : false; update(); }, 1200); }, { signal: this.#ac.signal });
    window.addEventListener('resize', update, { signal: this.#ac.signal });
    if (alwaysOn()) update(); // before the observer's first callback — first paint, not first scroll
    // Horizontal-scroll affordance on the compare table: drop the right-edge fade once fully scrolled.
    for (const wrap of document.querySelectorAll('[data-adm-scrollwrap]')) {
      const paint = () => { wrap.dataset.scrollEnd = String(wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 2); };
      wrap.addEventListener('scroll', paint, { passive: true, signal: this.#ac.signal });
      window.addEventListener('resize', paint, { signal: this.#ac.signal });
      paint();
    }
  }

  #setStickyVisible(show) {
    if (!this.#sticky) return;
    this.#sticky.hidden = !show;
    const root = this.closest('.adm');
    const h = show ? `${this.#sticky.offsetHeight}px` : '0px';
    if (root) root.style.setProperty('--sticky-h', h);
    document.documentElement.style.setProperty('--aromely-sticky-h', h);
  }

  /* ───────────────────────── colour (variant) ───────────────────────── */
  /** Same price, same everything — only the variant id, the name and the gallery change. */
  onColorChange(event) {
    const button = event.target.closest('[data-variant-id]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    this.dataset.variantId = button.dataset.variantId;
    this.dataset.variantTitle = button.dataset.variantTitle || '';
    this.dataset.variantAvailable = button.dataset.available || 'true';
    for (const b of this.refs.colorBtns || []) b.setAttribute('aria-pressed', String(b === button));
    if (this.refs.colorName) this.refs.colorName.textContent = button.dataset.variantTitle || '';
    this.closest('.adm')?.querySelector('aromely-diffuser-gallery')?.filter?.(button.dataset.color || '');
    this.render();
  }

  /* ───────────────────────── placement + fit helper ───────────────────────── */

  onPlaceChange(event) {
    const place = event.target.dataset.place;
    if (!place || place === this.#state.place) return;
    this.#state.place = place;
    this.render();
    // The placement choice only feeds the fit helper below; open it so tapping a tile has a
    // visible result instead of looking inert until the shopper finds the accordion.
    if (this.refs.fit && !this.refs.fit.open) this.refs.fit.open = true;
    if (this.refs.fitResult && !this.refs.fitResult.hidden) this.fitRun();
  }

  fitPreset(event) {
    const v = event.target.dataset.sqft;
    this.#fit.preset = v;
    this.querySelectorAll('[data-sqft]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.sqft === v ? 'true' : 'false'));
    if (this.refs.fitExactWrap) this.refs.fitExactWrap.hidden = v !== 'exact';
    if (v === 'exact') this.refs.fitExact?.focus({ preventScroll: true });
    this.fitRun();
  }

  fitSystems(event) {
    const n = Number(event.target.dataset.systems) || 1;
    this.#fit.systems = n;
    this.querySelectorAll('[data-systems]').forEach((b) => b.setAttribute('aria-pressed', Number(b.dataset.systems) === n ? 'true' : 'false'));
    this.fitRun();
  }

  fitContinue() {
    if (this.refs.fit) this.refs.fit.open = false;
    this.refs.addBtn?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.refs.addBtn?.focus({ preventScroll: true });
  }

  async #loadFitEngine() {
    if (this.#fit.engine || !this.dataset.fitEngineUrl) return this.#fit.engine;
    try {
      this.#fit.engine = await import(this.dataset.fitEngineUrl);
      const raw = JSON.parse(this.refs.fitUnits?.textContent || '[]');
      this.#fit.units = raw
        .map((u) => this.#fit.engine.diffuserQuizUnit(u.profile, { id: String(u.id), name: u.name, priceCents: u.priceCents, available: true, url: u.url, image: u.image }))
        .filter(Boolean);
    } catch (err) {
      console.warn('[aromely-diffuser-mvp] fit engine unavailable; hiding the helper', err);
      if (this.refs.fit) this.refs.fit.hidden = true;
    }
    return this.#fit.engine;
  }

  async fitRun() {
    const r = this.refs;
    if (!r.fitResult) return;
    const engine = await this.#loadFitEngine();
    if (!engine || !this.#fit.units) return;

    const fmt = (x) => Math.round(x).toLocaleString('en-US');
    const ceilingFt = Number(r.fitCeiling?.value) || 8;
    const ceilingLabel = r.fitCeiling?.selectedOptions?.[0]?.textContent?.replace(' (standard)', '').replace('About ', 'about ') || `${ceilingFt} ft`;
    const preset = this.#fit.preset;
    if (!preset) { r.fitResult.hidden = true; return; }

    let area = preset === 'exact' ? Math.round(Number(r.fitExact?.value)) : Number(preset);
    const name = this.dataset.productName || 'this diffuser';
    const show = (band, bandText, text, math, altHtml, contact) => {
      r.fitResult.hidden = false;
      r.fitResult.dataset.band = band || '';
      if (r.fitBand) r.fitBand.textContent = bandText;
      if (r.fitText) r.fitText.textContent = text;
      if (r.fitMath) r.fitMath.textContent = math || '';
      if (r.fitAlt) { r.fitAlt.hidden = !altHtml; r.fitAlt.innerHTML = altHtml || ''; }
      if (r.fitContact) r.fitContact.hidden = !contact;
    };

    if (preset === 'exact' && (!Number.isFinite(area) || area < 50)) {
      show('', 'One more thing', `Type the total square footage you want to scent. "${this.dataset.coverageFmt || ''} sq ft and up" isn't a number we can check, so we won't guess.`, '', '', false);
      return;
    }

    const hvac = this.#state.place === 'hvac';
    const input = { setup: hvac ? 'hvac' : 'room', context: 'home', area, ceilingFt, hvacCount: hvac ? this.#fit.systems : undefined, spaceType: 'open', publicAccess: false };
    const result = engine.evaluateDiffuserFit(input, this.#fit.units);
    if (!['match', 'margin', 'empty'].includes(result.status)) {
      show('', 'Hmm', 'We couldn\'t work that one out. Check the number and try again, or ask us.', '', '', true);
      return;
    }

    const pro = this.#fit.units.find((u) => String(u.id) === String(this.dataset.productId));
    if (!pro) { show('', '', 'Sizing data for this model is missing.', '', '', true); return; }
    const vol = pro.volumeCuft;
    const perSystem = result.perSystem;
    const target = result.target;
    const n = hvac ? this.#fit.systems : 1;
    const eq = Math.round(vol / ceilingFt / 50) * 50;
    const eqComfort = Math.round(vol / ceilingFt / engine.HEADROOM / 50) * 50;
    const perLabel = n > 1 ? ' per system' : '';
    const math = `${fmt(area)} sq ft × ${ceilingFt} ft ≈ ${fmt(area * ceilingFt)} cu ft of air${n > 1 ? `, about ${fmt(perSystem)} cu ft per system` : ''}. One ${name} scents up to ${fmt(vol)} cu ft. At ${ceilingLabel} ceilings that's about ${fmt(eq)} sq ft stated, ${fmt(eqComfort)} sq ft with our 1.2 allowance. We assume that ceiling height throughout, and the allowance is guidance, not a guarantee.`;
    const alt = (u, label) => u ? `<a href="${u.url}">${u.image ? `<img src="${u.image}" alt="" loading="lazy">` : '<span></span>'}<span><b>${u.name}</b><span>Up to ${fmt(u.publishedSqFt)} sq ft · ${this.#money(u.priceCents)}</span></span><u>${label}</u></a>` : '';
    const units = n > 1 ? `You'd need ${n}, one per HVAC system. ` : '';

    if (vol >= target) {
      const smaller = result.recommended && String(result.recommended.id) !== String(pro.id) && result.recommended.volumeCuft < vol ? result.recommended : null;
      show('A', 'Fits with room to spare',
        `Yes. The ${name} covers ${fmt(area)} sq ft at ${ceilingLabel} ceilings${perLabel} within our 1.2 allowance, so it doesn't have to run at its limit. ${units}`.trim(),
        math, smaller ? alt(smaller, 'Also fits') : '', false);
    } else if (vol >= perSystem) {
      const bigger = result.recommended && String(result.recommended.id) !== String(pro.id) ? result.recommended : null;
      show('B', 'Covers it, near its limit',
        `The ${name} is rated for this space, but with little to spare at ${ceilingLabel} ceilings. Expect to run it stronger, and a lighter scent in the farthest rooms. ${units}${bigger ? `For more margin, the ${bigger.name} covers more.` : 'For more margin, one unit per HVAC system spreads the load.'}`.trim(),
        math, alt(bigger, 'More capacity'), !bigger);
    } else {
      const bigger = result.recommended || result.alsoWorks || null;
      show('C', 'Beyond what one ' + name + ' covers',
        `${fmt(area)} sq ft at ${ceilingLabel} ceilings${perLabel} is more air than one ${name} is rated for. ${units}${bigger ? `The ${bigger.name} is the right size for this space.` : (hvac ? 'Splitting it across HVAC systems, one unit each, is the way to cover it. Or talk to us.' : 'Standalone, a space this size needs more than one diffuser. Talk to us.')}`.trim(),
        math, alt(bigger, 'See it'), true);
    }
  }
}

if (!customElements.get('aromely-diffuser-mvp')) {
  customElements.define('aromely-diffuser-mvp', AromelyDiffuserMvp);
}

